import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, MapPin } from "lucide-react";

export default function Hero({ status }) {
    const { t } = useTranslation();
    const heroBg =
        "https://customer-assets.emergentagent.com/job_polar-token-land/artifacts/tb0oi337_sfondo%20sito.jpg";

    const phaseKey = {
        early_access: "phase.early_access_live",
        public_sale: "phase.public_sale_live",
        ended: "phase.auctions_only",
        closed: "phase.closed",
    }[status?.current_phase];
    const phaseLabel = phaseKey ? t(phaseKey) : t("phase.loading");

    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[92vh] grain overflow-hidden flex items-end"
        >
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${heroBg})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-obsidian/10" />
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian/60 via-transparent to-obsidian/40" />

            <div className="relative max-w-7xl mx-auto w-full px-4 md:px-8 pb-16 md:pb-24 fade-up">
                <div className="flex items-center gap-3 mb-6">
                    <img
                        src="https://customer-assets.emergentagent.com/job_polar-token-land/artifacts/h5uomzcw_immagine.png"
                        alt="The Portal"
                        className="h-12 w-12 object-contain rounded-full ring-1 ring-frost/40 shadow-[0_0_25px_rgba(56,189,248,0.35)]"
                    />
                    <div>
                        <div className="text-xs uppercase tracking-[0.4em] text-frost flex items-center gap-2">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-frost opacity-75 pulse-dot" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-frost" />
                            </span>
                            {phaseLabel}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-1">
                            {t("hero.kicker")}
                        </div>
                    </div>
                </div>

                <h1 className="font-serif font-light tracking-tight text-5xl md:text-7xl lg:text-[8rem] leading-[0.95] max-w-5xl">
                    {t("hero.title_1")}
                    <br />
                    <span className="italic text-frost">{t("hero.title_2")}</span>
                </h1>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                    <p className="md:col-span-6 text-base font-mono text-slate-300 max-w-xl leading-relaxed">
                        {t("hero.tagline")}
                    </p>

                    <div className="md:col-span-6 flex flex-wrap gap-3 md:justify-end">
                        <Link
                            to="/explorer"
                            data-testid="hero-mint-cta"
                            className="btn-sharp inline-flex items-center gap-2 bg-white text-obsidian px-6 py-4 hover:bg-slate-200"
                        >
                            {t("hero.cta_mint")} <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/auctions"
                            data-testid="hero-auctions-cta"
                            className="btn-sharp inline-flex items-center gap-2 border border-white/30 text-white px-6 py-4 hover:bg-white/5 hover:border-gold hover:text-gold"
                        >
                            <MapPin className="w-4 h-4" /> {t("hero.cta_auctions")}
                        </Link>
                    </div>
                </div>

                {status && (
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {[
                            { label: t("hero.stats.supply"), value: "14,200" },
                            {
                                label: t("hero.stats.early"),
                                value: `${(status.early_access_total - status.early_access_sold).toLocaleString()} / 2,000`,
                                sub: "$25 USDC",
                            },
                            {
                                label: t("hero.stats.public"),
                                value: `${(status.public_sale_total - status.public_sale_sold).toLocaleString()} / 12,187`,
                                sub: "$80 USDC",
                            },
                            { label: t("hero.stats.top"), value: "13", sub: t("hero.stats.auction") },
                        ].map((s) => (
                            <div key={s.label} className="bg-obsidian/80 backdrop-blur-md p-6">
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                                    {s.label}
                                </div>
                                <div className="font-serif text-3xl mt-2">{s.value}</div>
                                {s.sub && (
                                    <div className="text-xs font-mono text-frost mt-1">{s.sub}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
